import { Dispatch, FC, memo, SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  DialogActions,
  DialogContent,
  FormControl,
  Typography,
  OutlinedInput,
  TableContainer,
  TableBody,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  ListItemText,
  Popper,
  Fade,
  Paper,
  ClickAwayListener,
} from "@material-ui/core";
import { closeDrawer } from "actions/drawerActions";
import { useDispatch } from "react-redux";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import { falconEditComment } from "actions/falconAction";
import {
  FalconCommentData,
  FalconCommentDataContent,
  FalconStandardData,
  FalconTableRow,
} from "types/Falcon";
import { useStyles } from "../../../pages/Falcon/styles";
import {
  TABLE_CORRECTION_COLS,
  TABLE_SIDE_ROWS,
  TABLE_SIDE_ROWS_TWO,
} from "../../../pages/Falcon/falconTable";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import CustomIconButton from "componentsNew/buttons/CustomIconButton";
import Delete from "@material-ui/icons/Delete";
import Add from "@material-ui/icons/Add";
import Edit from "@material-ui/icons/EditOutlined";
import Check from "@material-ui/icons/Check";
import Close from "@material-ui/icons/Close";
import { fleetSizesConstantList } from "constants/constants";
import { roleDa } from "mocks/roleDA";
import { DateTime } from "luxon";

interface EditFalconCommentDialogProps {
  date: string | number | undefined;
  data: FalconCommentData | undefined;
}

type CommentListSetter = Dispatch<SetStateAction<FalconCommentDataContent[]>>;

const ROUTE_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 250,
      width: 250,
    },
  },
  variant: "menu" as const,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
};

const SIZE_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 250,
      width: 100,
    },
  },
  variant: "menu" as const,
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom" as const, horizontal: "center" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "center" as const },
};

const sameCustomRowIds = (a: FalconCommentDataContent[], b: FalconCommentDataContent[]) =>
  a.length === b.length && a.every((item, index) => item.id === b[index].id);

export const FalconDrawerContent: FC<EditFalconCommentDialogProps> = ({ date, data }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const tableContainerRef = useRef(null);
  const [comment, setComment] = useState<string>(data?.report_comment || "");
  const commentListRef = useRef<FalconCommentDataContent[]>(data?.content || []);
  const [customComments, setCustomComments] = useState<FalconCommentDataContent[]>(() =>
    (data?.content || []).filter((item) => item.isCustom),
  );
  const [matchStatus, setMatchStatus] = useState<number>((data?.content || []).length ? 2 : 1);
  const [isDirty, setIsDirty] = useState(false);
  const [hasPending, setHasPending] = useState(() =>
    Boolean((data?.content || []).some((item) => item.isPending)),
  );
  const { falconData } = useTypeSelectors((state) => state.falconTable);

  const [initialCommentsById] = useState(() => {
    const map: Record<string, FalconCommentDataContent> = {};
    (data?.content || []).forEach((item) => {
      if (!item.isCustom) map[item.id] = item;
    });
    return map;
  });

  const setCommenList: CommentListSetter = useCallback((updater) => {
    const prev = commentListRef.current;
    const next = typeof updater === "function" ? updater(prev) : updater;
    commentListRef.current = next;

    const nextCustom = next.filter((item) => item.isCustom);
    setCustomComments((prevCustom) =>
      sameCustomRowIds(prevCustom, nextCustom) ? prevCustom : nextCustom,
    );

    const nextPending = next.some((item) => item.isPending);
    setHasPending((prevPending) => (prevPending === nextPending ? prevPending : nextPending));

    const nextMatchStatus = next.length ? 2 : 1;
    setMatchStatus((prevStatus) => (prevStatus === nextMatchStatus ? prevStatus : nextMatchStatus));

    setIsDirty(true);
  }, []);

  const handleSubmit = () => {
    dispatch(falconEditComment(date, comment, commentListRef.current, matchStatus));
    dispatch(closeDrawer());
  };

  const closenModal = () => dispatch(closeDrawer());

  const addCustomRow = () => {
    const el: any = tableContainerRef.current;
    if (el) {
      el.scrollTop = 314;
    }
    const now = new Date();
    setCommenList((prevCommentList) => [
      ...prevCommentList,
      {
        id: now
          .toISOString()
          .replace("T", "-")
          .replace("Z", "")
          .replace(/:/g, "-")
          .replace(/\./, "-"),
        route: "",
        size: "",
        hour: null,
        correction: 0,
        isCustom: true,
        isPending: true,
      },
    ]);
  };

  const standardListForDate = useMemo(
    () =>
      falconData?.standardList?.filter(
        (item: FalconStandardData) => item.report__report_date === date,
      ) || [],
    [falconData?.standardList, date],
  );

  const visibleRows = useMemo(
    () =>
      [...TABLE_SIDE_ROWS, ...TABLE_SIDE_ROWS_TWO].filter((row: FalconTableRow) =>
        standardListForDate.some(
          (item: FalconStandardData) =>
            item?.role === row?.role &&
            row?.hour?.includes(item?.role_hours) &&
            row?.vans?.includes(
              item?.van__fleet_size ? item?.van__fleet_size : item?.cancelled_fleet_size,
            ),
        ),
      ),
    [standardListForDate],
  );

  return (
    <>
      <DialogContent>
        <Box className={classes.correctionTableSection}>
          <TableContainer ref={tableContainerRef} className={classes.correctionTableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {TABLE_CORRECTION_COLS.map((item: string) => (
                    <TableCell key={item} className={`${classes.correctionTableHeadCell}`} colSpan={2}>
                      <Typography
                        className={`${classes.tableCellContent} ${classes.borderCellRightLight}`}
                      >
                        {item}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell
                    className={`${classes.correctionTableHeadCell}`}
                    colSpan={1}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.tableBody}>
                {visibleRows.map((row) => (
                  <FalconCorrectionTableRow
                    key={`correction-${row.id}`}
                    row={row}
                    currentCount={returnFalconTableCount(row, standardListForDate)}
                    commentData={initialCommentsById[row.id]}
                    setCommenList={setCommenList}
                  />
                ))}
                {customComments.map((item) => (
                  <FalconCorrectionTableisCustomRow
                    key={item.id}
                    commentData={item}
                    setCommenList={setCommenList}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box className={`${classes.addRow}`} onClick={addCustomRow}>
            <Add />
            <Typography>Add Row</Typography>
          </Box>
        </Box>
        <FormControl fullWidth className={classes.textareaSection}>
          <Typography className={classes.textareaLabel}>Comment</Typography>
          <OutlinedInput
            className={`${classes.input} ${classes.textarea}`}
            name="edit_comment"
            multiline
            defaultValue={comment}
            onChange={(e) => setComment(e.target.value)}
            minRows={6}
            maxRows={6}
          />
        </FormControl>
      </DialogContent>
      <DialogActions className={classes.actionSection}>
        <Typography className={classes.editedMessage}>
          {data?.editor__first_name
            ? `Last updated by ${data?.editor__first_name} ${data?.editor__last_name} on ${DateTime.fromISO(String(data.edited.split("T0")[0]) || "").toFormat("dd-MM-yyyy")}`
            : ""}
        </Typography>
        <Box className={classes.actionButtons}>
          <CustomButton
            buttonText={"Save"}
            onClick={handleSubmit}
            variant="contained"
            disabled={
              (!isDirty &&
                data?.is_match === matchStatus &&
                data?.report_comment === comment) ||
              hasPending
            }
          />
          <CustomButton buttonText={"Cancel"} onClick={closenModal} variant="text" />
        </Box>
      </DialogActions>
    </>
  );
};

const FalconCorrectionTableRow = memo(
  ({
    row,
    currentCount,
    commentData,
    setCommenList,
  }: {
    row: FalconTableRow;
    currentCount: number;
    commentData: FalconCommentDataContent | undefined;
    setCommenList: CommentListSetter;
  }) => {
    const classes = useStyles();
    const [count, setCount] = useState<number>(commentData ? commentData?.correction : currentCount);
    const [editMode, setEditMode] = useState<boolean>(false);

    const onChangeCorrection = (value: number) => {
      setCount(value);
      setCommenList((prevList) => {
        if (value === currentCount) {
          return prevList.filter((item) => item.id !== row.id);
        }
        const exists = prevList.some((item) => item.id === row.id);
        if (exists) {
          return prevList.map((item) => (item.id === row.id ? { ...item, correction: value } : item));
        }
        return [
          ...prevList,
          {
            id: row.id,
            route: row.label,
            size: row.sizeLabel,
            hour: row.hour[0],
            correction: value,
            isCustom: false,
          },
        ];
      });
    };

    const onDisregard = () => {
      setEditMode(false);
      setCount(currentCount);
      setCommenList((prevList) => prevList.filter((item) => item.id !== row.id));
    };

    return (
      <TableRow>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Typography className={`${classes.tableCellContent}`}>{row.label}</Typography>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Typography className={`${classes.tableCellContent} ${classes.sizeCell}`}>
            {row.sizeLabel}
          </Typography>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Typography className={`${classes.tableCellContent}`}>
            {row?.hour && row.hour?.length <= 2 ? row.hour : "n/a"}
          </Typography>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Box className={`${classes.tableCellContent}`}>
            {!editMode ? (
              <Typography className={`${classes.editField}`}>{count}</Typography>
            ) : (
              <OutlinedInput
                autoFocus
                className={classes.editField}
                name="plan_number"
                value={count}
                onChange={(e) => onChangeCorrection(Number(e.target.value.replace(/[^0-9]/g, "")))}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={1}>
          <Box className={`${classes.tableCellContent} ${classes.tableContentButtons}`}>
            {!editMode ? (
              <>
                <CustomIconButton
                  icon={<Edit />}
                  iconColor={"darkGray"}
                  theme={"gray"}
                  action={() => setEditMode(true)}
                />
                <CustomIconButton icon={<></>} iconColor={"darkGray"} theme={"gray"} disabled />
              </>
            ) : (
              <>
                <CustomIconButton
                  icon={<Check />}
                  iconColor={"white"}
                  theme={"blue"}
                  action={() => setEditMode(false)}
                />
                <CustomIconButton
                  icon={<Close />}
                  iconColor={"white"}
                  theme={"red"}
                  action={onDisregard}
                />
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  },
);

const FalconCorrectionTableisCustomRow = memo(
  ({
    commentData,
    setCommenList,
  }: {
    commentData: FalconCommentDataContent;
    setCommenList: CommentListSetter;
  }) => {
    const classes = useStyles();
    const [route, setRoute] = useState<string>(commentData?.route || "");
    const [size, setSize] = useState<string>(commentData?.size || "");
    const [hour, setHour] = useState<number | null | undefined>(commentData?.hour || 0);
    const [count, setCount] = useState<number>(commentData?.correction || 0);
    const [mode, setMode] = useState<string>(commentData.isPending ? "pending" : "created");
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const addComment = () => {
      setMode("created");
      setCommenList((prevList) =>
        prevList.map((item) => (item.id === commentData.id ? { ...item, isPending: false } : item)),
      );
    };

    const onChangeCorrection = (value: any, type: string, setAction: (arg: any) => void) => {
      setAction(value);
      setCommenList((prevList) =>
        prevList.map((item) => (item.id === commentData.id ? { ...item, [type]: value } : item)),
      );
    };

    const deleteCorrection = (id: string) => {
      if (anchorEl) {
        setAnchorEl(null);
      }
      setCommenList((prevList) => prevList.filter((item) => item.id !== id));
    };

    const onClickDeleteBtn = (event: React.MouseEvent<HTMLInputElement>) => {
      setAnchorEl(event.currentTarget);
    };

    return (
      <TableRow>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Box className={`${classes.tableCellContent}`}>
            {!["pending", "edit"].includes(mode) ? (
              <Typography>{route}</Typography>
            ) : (
              <Select
                fullWidth
                variant="outlined"
                value={route}
                onChange={(e) => onChangeCorrection(e.target.value, "route", setRoute)}
                MenuProps={ROUTE_MENU_PROPS}
              >
                {roleDa.map((spot) => (
                  <MenuItem key={spot.name} value={spot.name}>
                    <ListItemText primary={spot.name} />
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Box className={`${classes.tableCellContent}`}>
            {!["pending", "edit"].includes(mode) ? (
              <Typography className={`${classes.sizeCell}`}>{size}</Typography>
            ) : (
              <Select
                className={classes.sizeCell}
                variant="outlined"
                value={size}
                onChange={(e) => onChangeCorrection(e.target.value, "size", setSize)}
                MenuProps={SIZE_MENU_PROPS}
              >
                {fleetSizesConstantList.map((spot) => (
                  <MenuItem key={spot.id} value={spot.id}>
                    <ListItemText primary={spot.title} />
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Box className={`${classes.tableCellContent}`}>
            {!["pending", "edit"].includes(mode) ? (
              <Typography className={`${classes.editField}`}>{hour}</Typography>
            ) : (
              <OutlinedInput
                autoFocus
                className={classes.editField}
                value={hour}
                onChange={(e) => onChangeCorrection(e.target.value, "hour", setHour)}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Box className={`${classes.tableCellContent}`}>
            {!["pending", "edit"].includes(mode) ? (
              <Typography className={`${classes.editField}`}>{count}</Typography>
            ) : (
              <OutlinedInput
                autoFocus
                className={classes.editField}
                value={count}
                onChange={(e) =>
                  onChangeCorrection(
                    Number(e.target.value.replace(/[^0-9]/g, "")),
                    "correction",
                    setCount,
                  )
                }
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={1}>
          <Typography className={`${classes.tableCellContent} ${classes.tableCellButtons}`}>
            {["pending", "edit"].includes(mode) ? (
              <CustomIconButton
                icon={<Check />}
                iconColor={"white"}
                theme={"blue"}
                action={addComment}
                disabled={!route && !size && !hour && !count}
              />
            ) : (
              ""
            )}
            {mode === "pending" && (
              <>
                <CustomIconButton
                  icon={<Close />}
                  iconColor={"white"}
                  theme={"red"}
                  action={() => deleteCorrection(commentData.id)}
                />
              </>
            )}
            {mode === "created" && (
              <>
                <CustomIconButton
                  icon={<Edit />}
                  iconColor={"darkGray"}
                  theme={"gray"}
                  action={() => setMode("edit")}
                />
                <ClickAwayListener onClickAway={() => (anchorEl ? setAnchorEl(null) : null)}>
                  <Box>
                    <Popper
                      style={{ zIndex: 1 }}
                      open={Boolean(anchorEl)}
                      anchorEl={anchorEl}
                      placement={"bottom-end"}
                      disablePortal
                      transition
                    >
                      {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={350}>
                          <Paper className={classes.popperPaper}>
                            <Typography>Delete this row?</Typography>
                            <Box style={{ display: "flex", gap: 8 }}>
                              <CustomButton
                                variant="text"
                                buttonText={"Cancel"}
                                onClick={() => setAnchorEl(null)}
                              />
                              <CustomButton
                                variant="contained"
                                buttonText={"Delete"}
                                onClick={() => deleteCorrection(commentData.id)}
                                customClass={classes.redTheme}
                              />
                            </Box>
                          </Paper>
                        </Fade>
                      )}
                    </Popper>
                    <CustomIconButton
                      key={`comment-${commentData.id}`}
                      icon={<Delete />}
                      iconColor={"red"}
                      theme={"blank"}
                      action={(e: any) => onClickDeleteBtn(e)}
                    />
                  </Box>
                </ClickAwayListener>
              </>
            )}
            {mode === "edit" && (
              <>
                <CustomIconButton
                  icon={<Close />}
                  iconColor={"white"}
                  theme={"red"}
                  action={() => setMode("created")}
                />
              </>
            )}
          </Typography>
        </TableCell>
      </TableRow>
    );
  },
);

export const returnFalconTableCount = (
  tableRow: FalconTableRow,
  falconData: FalconStandardData[],
) => {
  switch (tableRow.id) {
    case "client-cancelled-cargo":
    case "client-cancelled-large":
    case "client-cancelled-cube":
    case "client-cancelled-medium":
    case "mycu-cancelled-cargo":
    case "mycu-cancelled-large":
    case "mycu-cancelled-cube":
    case "mycu-cancelled-medium":
      return falconData
        ?.filter(
          (item: FalconStandardData) =>
            tableRow.role === item.role &&
            tableRow?.vans?.includes(
              item?.van__fleet_size ? item?.van__fleet_size : item?.cancelled_fleet_size,
            ),
        )
        .reduce((a: number, b: FalconStandardData) => a + b.routes, 0);

    default:
      return falconData
        .filter(
          (item: FalconStandardData) =>
            item?.role === tableRow.role &&
            tableRow?.vans?.includes(item?.van__fleet_size) &&
            tableRow.hour?.includes(item?.role_hours),
        )
        ?.reduce((a: number, b: FalconStandardData) => a + b.routes, 0);
  }
};